import React from "react";
import { styled } from '@mui/material/styles';
import {
  AppBar,
  Box,
  Button,
  Container,
  FormControl,
  Input,
  InputAdornment,
  InputLabel,
  Tab,
  Tabs,
} from "@mui/material";
import { Typography, TextField } from "@mui/material";
import {ImageSearch, Language, VideoLibrary} from "@mui/icons-material";
import {useViewport} from "../../helpers";
import {useTranslation} from "react-i18next";

const PREFIX = 'UploadForm';

const classes = {
  root: `${PREFIX}-root`,
  tabs: `${PREFIX}-tabs`,
  uploadForm: `${PREFIX}-uploadForm`,
  buttonUpload: `${PREFIX}-buttonUpload`,
  preview: `${PREFIX}-preview`,
  previewObj: `${PREFIX}-previewObj`,
  tabPanel: `${PREFIX}-tabPanel`,
  inputsContainer: `${PREFIX}-inputsContainer`
};

const StyledContainer = styled(Container)((
  {
    theme
  }
) => ({
  [`& .${classes.root}`]: {
    [theme.breakpoints.up('md')]: {
      flexGrow: 1,
      display: 'flex',
    },
    backgroundColor: '#424242',
  },

  [`& .${classes.tabs}`]: {
    borderRight: `1px solid ${theme.palette.divider}`,
    flex: 1,
  },

  [`& .${classes.uploadForm}`]: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },

  [`& .${classes.buttonUpload}`]: {
    marginLeft: theme.spacing(1)
  },

  [`& .${classes.preview}`]: {
    marginTop: '1rem',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  [`& .${classes.previewObj}`]: {
    maxWidth: '100%',
    maxHeight: 400,
  },

  [`& .${classes.tabPanel}`]: {
    flex: 2,
  },

  [`& .${classes.inputsContainer}`]: {
    marginBottom: '1rem'
  }
}));

function TabPanel(props) {
  const {children, value, index, ...other} = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box p={3}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `vertical-tab-${index}`,
    'aria-controls': `vertical-tabpanel-${index}`,
  };
}

export default function UploadForm(props) {

  const {width} = useViewport();
  const {t} = useTranslation();
  const {video, image, link} = {
    video: props.file && props.file?.type?.startsWith("video"),
    image: props.file && props.file?.type?.startsWith("image"),
    link: !props.file && props.link
  }
  const [data, setData] = React.useState({
    file: props.file,
    url: props.file ? URL.createObjectURL(props.file) : "",
    video: video || false,
    image: image || false,
    link: link || false,
    value: video ? 0 : image ? 1 : link ? 2 : 0,
  });
  const [title, setTitle] = React.useState('');
  const [description, setDescription] = React.useState('');


  const handleChange = (event, newValue) => {
    setData({...data, value: newValue});
  };

  function handleFileChange(event, type) {
    if (event.target?.files?.length > 0) {
      if (data?.url) {
        URL.revokeObjectURL(data.url);
      }
      setData({
        ...data,
        video: type === 'video',
        image: type === 'image',
        file: event.target.files[0],
        url: URL.createObjectURL(event.target.files[0]),
      });
      props.save(event.target.files[0]);
    }
  }

  function isURL(str) {
    return /^(?:\w+:)?\/\/([^\s\.]+\.\S{2}|localhost[\:?\d]*)\S*$/.test(str);
  }

  function handleChangeUrl(e) {
    if (isURL(e.target.value)) {
      props.saveLink(e.target.value);
      setData({...data, link: e.target.value, errorLink: false});
    } else {
      setData({...data, link: e.target.value, errorLink: true});
    }
  }

  function handleChangeInfos(value, key) {
    if (key === 'title') {
      setTitle(value);
      props.saveTitle(value)
    } else {
      setDescription(value);
      props.saveDescription(value);
    }
  }

  return (
    <StyledContainer disableGutters>
      <div className={classes.root}>
        {width <= 768 ?
          <AppBar position="static" color="transparent">
            <Tabs value={data.value} onChange={handleChange} aria-label="wrapped tabs"
                  indicatorColor="primary"
                  textColor="primary">
              <Tab label={t('uploadForm.video')} {...a11yProps(0)} />
              <Tab label={t('uploadForm.image')} {...a11yProps(1)} />
              <Tab label={t('uploadForm.url')} {...a11yProps(2)} />
            </Tabs>
          </AppBar>
          :
          <Tabs
            orientation="vertical"
            variant="scrollable"
            value={data.value}
            onChange={handleChange}
            aria-label="Upload Content Type"
            className={classes.tabs}
            indicatorColor="primary"
          >
            <Tab label={t('uploadForm.video')} {...a11yProps(0)} />
            <Tab label={t('uploadForm.image')} {...a11yProps(1)} />
            <Tab label={t('uploadForm.url')} {...a11yProps(2)} />
          </Tabs>
        }
        <TabPanel value={data.value} index={0} className={classes.tabPanel}>
          <div className={classes.inputsContainer}>
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              id="title"
              label="Title"
              name="title"
              autoComplete="title"
              value={title}
              onChange={e => handleChangeInfos(e.target.value, 'title')}
            />
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              id="description"
              label="Description"
              name="description"
              autoComplete="description"
              value={description}
              onChange={e => handleChangeInfos(e.target.value, 'description')}
            />
          </div>
          <div className={classes.uploadForm}>
            <Button
              className={classes.buttonUpload}
              variant="contained"
              color="primary"
              component="label"
              onChange={(e) => handleFileChange(e, 'video')}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <VideoLibrary/>
              <input
                type="file"
                accept="video/avi, video/wmv, video/mov, video/rm, video/ram, video/swf, video/flv, video/ogg, video/webm, video/mp4"
                name="fileUpload"
                hidden
              />
            </Button>
            <Typography variant="body1">
              {data?.file?.fileName}
            </Typography>
          </div>
          {data?.url && data.video &&
          <div className={classes.preview}>
            <video controls className={classes.previewObj}>
              <source src={data.url}
                      type={data?.file.type}>
              </source>
            </video>
          </div>
          }
        </TabPanel>
        <TabPanel value={data.value} index={1} className={classes.tabPanel}>
        <div className={classes.inputsContainer}>
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              id="title"
              label="Title"
              name="title"
              autoComplete="title"
              value={title}
              onChange={e => handleChangeInfos(e.target.value, 'title')}
            />
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              id="description"
              label="Description"
              name="description"
              autoComplete="description"
              value={description}
              onChange={e => handleChangeInfos(e.target.value, 'description')}
            />
          </div>
          <div className={classes.uploadForm}>
            <Button
              className={classes.buttonUpload}
              variant="contained"
              color="primary"
              component="label"
              onChange={(e) => handleFileChange(e, 'image')}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <ImageSearch/>
              <input
                type="file"
                accept="image/*"
                name="imageUpload"
                hidden
              />
            </Button>
            <Typography variant="body1">
              {data?.file?.fileName}
            </Typography>
          </div>
          {data?.url && data.image &&
          <div className={classes.preview}>
            <img
              id="preview-image"
              alt={"upload"}
              src={data.url}
              className={classes.previewObj}
            />
          </div>
          }
        </TabPanel>
        <TabPanel value={data.value} index={2} className={classes.tabPanel}>
        <div className={classes.inputsContainer}>
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              id="title"
              label="Title"
              name="title"
              autoComplete="title"
              value={title}
              onChange={e => handleChangeInfos(e.target.value, 'title')}
            />
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              id="description"
              label="Description"
              name="description"
              autoComplete="description"
              value={description}
              onChange={e => handleChangeInfos(e.target.value, 'description')}
            />
          </div>
          <FormControl className={classes.margin} fullWidth>
            <InputLabel htmlFor="input-link-url-for-zone">{t('uploadForm.url')}</InputLabel>
            <Input
              id="input-link-url-for-zone"
              startAdornment={
                <InputAdornment position="start">
                  <Language/>
                </InputAdornment>
              }
              onChange={handleChangeUrl}
              error={data.errorLink}
            />
          </FormControl>
          {data.errorLink && <div className={classes.error}>{t('uploadForm.urlError')}</div>}
          {data?.link && !data.errorLink &&
          <div className={classes.preview}>
            <Button variant="outlined" color="primary" href={data.link} target="_blank">
              {t('uploadForm.preview')}
            </Button>
          </div>
          }
        </TabPanel>
      </div>
    </StyledContainer>
  );
}
